import * as React from "react";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import { useAppDispatch } from "../../../../store/hooks";
import {
  checkCanReach,
  clearReachError,
  fetchDataItems,
  fetchRegions,
  fetchServices,
  fetchTypes,
  selectRegion,
  selectService,
  selectType,
  setCanReach,
  setCloudaccountLink,
} from "../../../../store/slices/browserServiceSlice";

export interface VolumeBrowserService {
  volumeId: string;
  setVolumeId: React.Dispatch<React.SetStateAction<string>>;
  page: "none" | "provider" | "bucket";
  setPage: React.Dispatch<React.SetStateAction<"none" | "provider" | "bucket">>;
  provider: "aws" | "azure" | "gcp";
  setProvider: (value: "aws" | "azure" | "gcp") => void;
  cloudAccountName: string;
  setCloudAccountName: (value: string) => Promise<void>;
  isCheckingReach: boolean;
  isPreparing: boolean;
  prepareError: string;
  prepare: () => Promise<void>;
}

export function useVolumeBrowserService() {
  const dispatch = useAppDispatch();

  const [volumeId, setVolumeId] = React.useState("");
  const [page, setPage] = React.useState<"none" | "provider" | "bucket">("none");
  const [provider, _setProvider] = React.useState<"aws" | "azure" | "gcp">("aws");
  const [cloudAccountName, _setCloudAccountName] = React.useState("");
  const [isCheckingReach, setIsCheckingReach] = React.useState(false);
  const [isPreparing, setIsPreparing] = React.useState(false);
  const [prepareError, setPrepareError] = React.useState("");

  async function prepare() {
    try {
      setIsPreparing(true);
      setPrepareError("");
      const services = await dispatch(fetchServices()).unwrap();

      if (provider === "aws") {
        const service_s3 = services.find((service) => service.href.includes("/s3"));
        if (!service_s3) {
          setIsPreparing(false);
          setPrepareError("No S3 service is found");
          return;
        }
        dispatch(selectService(service_s3.href));
      }

      if (provider === "azure" || provider === "gcp") {
        const service_storage = services.find((service) => service.href.includes("/storage"));
        if (!service_storage) {
          setIsPreparing(false);
          setPrepareError("No storage service is found");
          return;
        }
        dispatch(selectService(service_storage.href));
      }

      const regions = await dispatch(fetchRegions()).unwrap();
      const region_global = regions.find((service) => service.href.includes("/global"));
      if (!region_global) {
        setIsPreparing(false);
        setPrepareError("No global region is found");
        return;
      }
      dispatch(selectRegion(region_global.href));
      const types = await dispatch(fetchTypes()).unwrap();

      if (provider === "aws" || provider === "gcp") {
        const type_bucket = types.find((service) => service.href.includes("/bucket"));
        if (!type_bucket) {
          setIsPreparing(false);
          setPrepareError("No bucket type is found");
          return;
        }
        dispatch(selectType(type_bucket.href));
      }
      if (provider === "azure") {
        const type_account = types.find((service) => service.href.includes("/account"));
        if (!type_account) {
          setIsPreparing(false);
          setPrepareError("No account type is found");
          return;
        }
        dispatch(selectType(type_account.href));
      }

      await dispatch(fetchDataItems());
      setPage("bucket");
      setTimeout(() => {
        setIsPreparing(false);
        setPrepareError("");
      }, 100);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      setPrepareError(errorMessage);
      setIsPreparing(false);
    }
  }

  async function setCloudAccountName(value: string) {
    setPrepareError("");
    dispatch(clearReachError());
    dispatch(setCanReach(false));
    _setCloudAccountName(value);
    if (value) {
      const { absolute: cloudaccountLink } = parseLink(value, { kind: "cloudaccount" });
      dispatch(setCloudaccountLink(cloudaccountLink));
      setIsCheckingReach(true);
      await dispatch(checkCanReach());
      setIsCheckingReach(false);
    } else {
      dispatch(clearReachError());
    }
  }

  function setProvider(value: "aws" | "azure" | "gcp") {
    _setProvider(value);
    setCloudAccountName("");
  }

  return {
    volumeId,
    setVolumeId,
    page,
    setPage,
    provider,
    setProvider,
    cloudAccountName,
    setCloudAccountName,
    isCheckingReach,
    isPreparing,
    prepareError,
    prepare,
  };
}
